import { NavigationError, Router } from '@angular/router';
import { DEFAULT_PROGRESSIVE_RETRY_DELAYS_MS } from '@ts-helpers/progressive-retry';
import { firstValueFrom, Observable } from 'rxjs';

const DEFAULT_MAX_RELOADS = 5;
const MAX_CHUNK_FETCH_ATTEMPTS_PER_CHUNK = 5;
const HARD_NAVIGATION_DELAY_MS = 2_000;
const HARD_NAVIGATION_RETRY_COOLDOWN_MS = 5 * 60_000;
const CHUNK_STORAGE_SCOPE = 'chunk';
const CHUNK_FETCH_STORAGE_SCOPE = 'chunk-fetch';
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /chunkloaderror/i,
  /loading chunk/i,
  /invalid javascript mime type/i,
  /not a valid javascript mime type/i,
] as const;

const navigationRetryCounts = new Map<string, number>();
const reloadCounts = new Map<string, number>();
const activeChunkFetchRecoveryKeys = new Set<string>();
const shownChunkLoadErrorDialogKeys = new Set<string>();

type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type RecoveryLocation = Pick<Location, 'reload'> & Partial<Pick<Location, 'assign'>>;
type WindowNameStore = Pick<Window, 'name'>;
type ChunkRecoveryFetch = (
  url: string,
  init: RequestInit
) => Promise<Pick<Response, 'ok' | 'status'>>;

interface ChunkRecoveryNetwork {
  online: () => boolean;
  waitUntilOnline: () => Observable<true>;
  reportNetworkFailure: () => void;
}

interface ChunkRecoveryLoadingIndicator {
  showLoading: (hideContent?: boolean) => void;
  hideLoading: () => void;
}

type ChunkLoadErrorDialogOpener = () => void;

interface ChunkRecoveryRuntime {
  router: Pick<Router, 'navigateByUrl'>;
  network?: ChunkRecoveryNetwork;
  loadingIndicator?: ChunkRecoveryLoadingIndicator;
  openChunkLoadErrorDialog?: ChunkLoadErrorDialogOpener;
}

interface HardNavigationState {
  attempts: number;
  cooldownUntil: number;
}

export interface ChunkNavigationErrorHandlerConfig {
  version: string;
  getRouter: () => Pick<Router, 'navigateByUrl'>;
  network?: ChunkRecoveryNetwork;
  getNetwork?: () => ChunkRecoveryNetwork;
  loadingIndicator?: ChunkRecoveryLoadingIndicator;
  getLoadingIndicator?: () => ChunkRecoveryLoadingIndicator;
  openChunkLoadErrorDialog?: ChunkLoadErrorDialogOpener;
  getOpenChunkLoadErrorDialog?: () => ChunkLoadErrorDialogOpener;
  storage?: RecoveryStorage;
  windowNameStore?: WindowNameStore;
  location?: RecoveryLocation;
  retryDelaysMs?: readonly number[];
  maxReloads?: number;
  fetchFn?: ChunkRecoveryFetch;
  setTimeoutFn?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout> | number;
  nowFn?: () => number;
}

export function isChunkLoadError(error: unknown): boolean {
  const message = getErrorMessage(error);

  if (!message) {
    return false;
  }

  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }

  if (!isErrorRecord(error)) {
    return '';
  }

  return [
    getErrorMessage(error['message']),
    getErrorMessage(error['reason']),
    getErrorMessage(error['error']),
  ]
    .filter(Boolean)
    .join(': ');
}

function isErrorRecord(error: unknown): error is Record<string, unknown> {
  return typeof error === 'object' && error !== null;
}

export function createChunkNavigationErrorHandler(
  config: ChunkNavigationErrorHandlerConfig
): (event: NavigationError) => void {
  const retryDelaysMs = config.retryDelaysMs ?? DEFAULT_PROGRESSIVE_RETRY_DELAYS_MS;
  const maxReloads = config.maxReloads ?? DEFAULT_MAX_RELOADS;
  const setTimeoutFn = config.setTimeoutFn ?? setTimeout;

  return (event: NavigationError): void => {
    if (!isChunkLoadError(event.error)) {
      return;
    }

    if (isChunkFetchRecoveryActive(config, event.url, event.error)) {
      return;
    }

    const network = getNetwork(config);

    if (network && !network.online()) {
      retryOfflineNavigation(config, getChunkRecoveryRuntime(config, network), event.url);
      return;
    }

    const retryKey = getNavigationRetryKey(config, event.url);
    const retryCount = navigationRetryCounts.get(retryKey) ?? 0;
    const delayMs = retryDelaysMs[retryCount];

    if (delayMs !== undefined) {
      navigationRetryCounts.set(retryKey, retryCount + 1);
      const runtime = getChunkRecoveryRuntime(config, network);

      setTimeoutFn(() => {
        runtime.router
          .navigateByUrl(event.url)
          .then(navigated => {
            if (!navigated) {
              scheduleHardNavigateToUrl(config, runtime, event.url, event.error);
            }
          })
          .catch(error => {
            if (isChunkLoadError(error)) {
              scheduleHardNavigateToUrl(config, runtime, event.url, error);
              return;
            }

            console.warn('[ChunkRecovery] Navigation retry failed.', error);
          });
      }, delayMs);

      return;
    }

    reloadAfterRetries(config, maxReloads);
  };
}

function retryOfflineNavigation(
  config: ChunkNavigationErrorHandlerConfig,
  runtime: ChunkRecoveryRuntime,
  url: string
): void {
  const network = runtime.network;

  if (!network) {
    return;
  }

  network.reportNetworkFailure();
  const loadingIndicator = runtime.loadingIndicator;

  loadingIndicator?.showLoading(true);

  firstValueFrom(network.waitUntilOnline())
    .then(() => runtime.router.navigateByUrl(url))
    .then(navigated => {
      if (!navigated) {
        scheduleHardNavigateToUrl(config, runtime, url);
      }
    })
    .catch(error => {
      if (isChunkLoadError(error)) {
        scheduleHardNavigateToUrl(config, runtime, url, error);
        return;
      }

      console.warn('[ChunkRecovery] Offline navigation retry failed.', error);
    })
    .finally(() => {
      loadingIndicator?.hideLoading();
    });
}

function getChunkRecoveryRuntime(
  config: ChunkNavigationErrorHandlerConfig,
  network?: ChunkRecoveryNetwork
): ChunkRecoveryRuntime {
  return {
    router: config.getRouter(),
    network,
    loadingIndicator: getLoadingIndicator(config),
    openChunkLoadErrorDialog: getOpenChunkLoadErrorDialog(config),
  };
}

function getNetwork(config: ChunkNavigationErrorHandlerConfig): ChunkRecoveryNetwork | undefined {
  return config.network ?? config.getNetwork?.();
}

function getLoadingIndicator(
  config: ChunkNavigationErrorHandlerConfig
): ChunkRecoveryLoadingIndicator | undefined {
  return config.loadingIndicator ?? config.getLoadingIndicator?.();
}

function getOpenChunkLoadErrorDialog(
  config: ChunkNavigationErrorHandlerConfig
): ChunkLoadErrorDialogOpener | undefined {
  return config.openChunkLoadErrorDialog ?? config.getOpenChunkLoadErrorDialog?.();
}

function getNavigationRetryKey(config: ChunkNavigationErrorHandlerConfig, url: string): string {
  return `${config.version}:${url}`;
}

function resetNavigationRetryCount(config: ChunkNavigationErrorHandlerConfig, url: string): void {
  navigationRetryCounts.delete(getNavigationRetryKey(config, url));
}

function reloadAfterRetries(config: ChunkNavigationErrorHandlerConfig, maxReloads: number): void {
  if (maxReloads <= 0) {
    return;
  }

  const storageKey = `stretchit:chunk-recovery:${config.version}:${CHUNK_STORAGE_SCOPE}`;
  const storage = getStorage(config);
  const windowNameStore = getWindowNameStore(config);
  const reloadCount =
    getStorageReloadCount(storage, storageKey) ??
    Math.max(
      getWindowNameReloadCount(windowNameStore, storageKey),
      reloadCounts.get(storageKey) ?? 0
    );

  if (reloadCount >= maxReloads) {
    return;
  }

  const nextReloadCount = reloadCount + 1;
  reloadCounts.set(storageKey, nextReloadCount);
  setReloadCount(storage, storageKey, nextReloadCount);
  setWindowNameReloadCount(windowNameStore, storageKey, nextReloadCount);
  getLocation(config).reload();
}

function getStorage(config: ChunkNavigationErrorHandlerConfig): RecoveryStorage | null {
  if (config.storage !== undefined) {
    return config.storage;
  }

  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

function getLocation(config: ChunkNavigationErrorHandlerConfig): RecoveryLocation {
  return config.location ?? globalThis.location;
}

function hardNavigateToUrl(config: ChunkNavigationErrorHandlerConfig, url: string): void {
  const location = getLocation(config);

  if (location.assign) {
    location.assign(url);
    return;
  }

  location.reload();
}

function scheduleHardNavigateToUrl(
  config: ChunkNavigationErrorHandlerConfig,
  runtime: ChunkRecoveryRuntime,
  url: string,
  error?: unknown
): void {
  resetNavigationRetryCount(config, url);

  const storageKey = getHardNavigationStorageKey(config, url, error);
  const state = getHardNavigationState(config, storageKey);
  const now = getNow(config);
  const chunkResource = getChunkResource(error);

  if (state.cooldownUntil > now) {
    showChunkLoadErrorDialog(runtime, storageKey);
    return;
  }

  if (state.cooldownUntil > 0) {
    clearHardNavigationState(config, storageKey);
    shownChunkLoadErrorDialogKeys.delete(storageKey);
    state.attempts = 0;
    state.cooldownUntil = 0;
  }

  if (!chunkResource) {
    const setTimeoutFn = config.setTimeoutFn ?? setTimeout;

    runtime.loadingIndicator?.showLoading(true);
    setTimeoutFn(() => hardNavigateToUrl(config, url), HARD_NAVIGATION_DELAY_MS);
    return;
  }

  if (activeChunkFetchRecoveryKeys.has(storageKey)) {
    return;
  }

  activeChunkFetchRecoveryKeys.add(storageKey);
  runtime.loadingIndicator?.showLoading(true);
  scheduleChunkFetchAttempt(config, runtime, url, chunkResource, storageKey);
}

function scheduleChunkFetchAttempt(
  config: ChunkNavigationErrorHandlerConfig,
  runtime: ChunkRecoveryRuntime,
  url: string,
  chunkResource: string,
  storageKey: string
): void {
  const state = getHardNavigationState(config, storageKey);
  const now = getNow(config);

  if (state.cooldownUntil > now) {
    showChunkLoadErrorDialog(runtime, storageKey);
    return;
  }

  if (state.attempts >= MAX_CHUNK_FETCH_ATTEMPTS_PER_CHUNK) {
    const nextState = {
      attempts: state.attempts,
      cooldownUntil: now + HARD_NAVIGATION_RETRY_COOLDOWN_MS,
    };

    setHardNavigationState(config, storageKey, nextState);
    showChunkLoadErrorDialog(runtime, storageKey);
    return;
  }

  setHardNavigationState(config, storageKey, {
    attempts: state.attempts + 1,
    cooldownUntil: 0,
  });

  const setTimeoutFn = config.setTimeoutFn ?? setTimeout;
  const retryDelaysMs = config.retryDelaysMs ?? DEFAULT_PROGRESSIVE_RETRY_DELAYS_MS;
  const delayMs = retryDelaysMs[state.attempts] ?? retryDelaysMs.at(-1) ?? HARD_NAVIGATION_DELAY_MS;

  setTimeoutFn(() => {
    fetchChunkResource(config, chunkResource)
      .then(available => {
        if (available) {
          clearChunkFetchRecoveryState(config, storageKey);
          hardNavigateToUrl(config, url);
          return;
        }

        scheduleChunkFetchAttempt(config, runtime, url, chunkResource, storageKey);
      })
      .catch(() => {
        scheduleChunkFetchAttempt(config, runtime, url, chunkResource, storageKey);
      });
  }, delayMs);
}

function clearChunkFetchRecoveryState(
  config: ChunkNavigationErrorHandlerConfig,
  storageKey: string
): void {
  clearHardNavigationState(config, storageKey);
  activeChunkFetchRecoveryKeys.delete(storageKey);
  shownChunkLoadErrorDialogKeys.delete(storageKey);
}

function showChunkLoadErrorDialog(runtime: ChunkRecoveryRuntime, storageKey: string): void {
  activeChunkFetchRecoveryKeys.delete(storageKey);
  runtime.loadingIndicator?.hideLoading();

  if (shownChunkLoadErrorDialogKeys.has(storageKey)) {
    return;
  }

  shownChunkLoadErrorDialogKeys.add(storageKey);
  runtime.openChunkLoadErrorDialog?.();
}

function fetchChunkResource(
  config: ChunkNavigationErrorHandlerConfig,
  chunkResource: string
): Promise<boolean> {
  const fetchFn: ChunkRecoveryFetch =
    config.fetchFn ?? ((url, init) => globalThis.fetch(url, init));

  return fetchFn(chunkResource, {
    cache: 'no-store',
    credentials: 'same-origin',
  }).then(response => response.ok || response.status === 304);
}

function getHardNavigationStorageKey(
  config: ChunkNavigationErrorHandlerConfig,
  url: string,
  error?: unknown
): string {
  return `stretchit:chunk-recovery:${config.version}:${CHUNK_FETCH_STORAGE_SCOPE}:${
    getChunkResource(error) ?? url
  }`;
}

function isChunkFetchRecoveryActive(
  config: ChunkNavigationErrorHandlerConfig,
  url: string,
  error?: unknown
): boolean {
  return activeChunkFetchRecoveryKeys.has(getHardNavigationStorageKey(config, url, error));
}

function getChunkResource(error: unknown): string | null {
  const message = getErrorMessage(error);
  const match = message.match(/(?:https?:\/\/|\/)?[^'"`\s)]+\.js\b/i);

  return match?.[0] ?? null;
}

function getHardNavigationState(
  config: ChunkNavigationErrorHandlerConfig,
  storageKey: string
): HardNavigationState {
  return {
    attempts:
      getStorageReloadCount(getStorage(config), `${storageKey}:attempts`) ??
      getWindowNameReloadCount(getWindowNameStore(config), `${storageKey}:attempts`),
    cooldownUntil:
      getStorageReloadCount(getStorage(config), `${storageKey}:cooldownUntil`) ??
      getWindowNameReloadCount(getWindowNameStore(config), `${storageKey}:cooldownUntil`),
  };
}

function setHardNavigationState(
  config: ChunkNavigationErrorHandlerConfig,
  storageKey: string,
  state: HardNavigationState
): void {
  const storage = getStorage(config);
  const windowNameStore = getWindowNameStore(config);

  setReloadCount(storage, `${storageKey}:attempts`, state.attempts);
  setReloadCount(storage, `${storageKey}:cooldownUntil`, state.cooldownUntil);
  setWindowNameReloadCount(windowNameStore, `${storageKey}:attempts`, state.attempts);
  setWindowNameReloadCount(windowNameStore, `${storageKey}:cooldownUntil`, state.cooldownUntil);
}

function clearHardNavigationState(
  config: ChunkNavigationErrorHandlerConfig,
  storageKey: string
): void {
  const storage = getStorage(config);
  const windowNameStore = getWindowNameStore(config);

  removeStorageValue(storage, `${storageKey}:attempts`);
  removeStorageValue(storage, `${storageKey}:cooldownUntil`);
  removeWindowNameReloadCount(windowNameStore, `${storageKey}:attempts`);
  removeWindowNameReloadCount(windowNameStore, `${storageKey}:cooldownUntil`);
}

function getNow(config: ChunkNavigationErrorHandlerConfig): number {
  return config.nowFn?.() ?? Date.now();
}

function getWindowNameStore(config: ChunkNavigationErrorHandlerConfig): WindowNameStore | null {
  if (config.windowNameStore !== undefined) {
    return config.windowNameStore;
  }

  try {
    return globalThis.window;
  } catch {
    return null;
  }
}

function getStorageReloadCount(storage: RecoveryStorage | null, storageKey: string): number | null {
  if (!storage) {
    return null;
  }

  try {
    const storedValue = storage.getItem(storageKey);

    if (storedValue === undefined || storedValue === null) {
      return 0;
    }

    const parsedValue = Number.parseInt(storedValue, 10);

    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
  } catch {
    return null;
  }
}

function setReloadCount(
  storage: RecoveryStorage | null,
  storageKey: string,
  reloadCount: number
): void {
  try {
    storage?.setItem(storageKey, String(reloadCount));
  } catch {
    return;
  }
}

function removeStorageValue(storage: RecoveryStorage | null, storageKey: string): void {
  try {
    storage?.removeItem(storageKey);
  } catch {
    return;
  }
}

function getWindowNameReloadCount(
  windowNameStore: WindowNameStore | null,
  storageKey: string
): number {
  return readWindowNameReloadCounts(windowNameStore)[storageKey] ?? 0;
}

function setWindowNameReloadCount(
  windowNameStore: WindowNameStore | null,
  storageKey: string,
  reloadCount: number
): void {
  if (!windowNameStore) {
    return;
  }

  try {
    windowNameStore.name = JSON.stringify({
      ...readWindowNameReloadCounts(windowNameStore),
      [storageKey]: reloadCount,
    });
  } catch {
    return;
  }
}

function removeWindowNameReloadCount(
  windowNameStore: WindowNameStore | null,
  storageKey: string
): void {
  if (!windowNameStore) {
    return;
  }

  try {
    const counts = readWindowNameReloadCounts(windowNameStore);
    delete counts[storageKey];
    windowNameStore.name = JSON.stringify(counts);
  } catch {
    return;
  }
}

function readWindowNameReloadCounts(
  windowNameStore: WindowNameStore | null
): Record<string, number> {
  if (!windowNameStore?.name) {
    return {};
  }

  try {
    const parsedValue: unknown = JSON.parse(windowNameStore.name);

    if (!isRecord(parsedValue)) {
      return {};
    }

    return Object.entries(parsedValue).reduce<Record<string, number>>((counts, [key, value]) => {
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        counts[key] = value;
      }

      return counts;
    }, {});
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
